import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatStepper } from '@angular/material/stepper';
import { ActivatedRoute, Router } from '@angular/router';
import { forkJoin, Observable, Subject } from 'rxjs';
import { finalize, flatMap, map, takeUntil } from 'rxjs/operators';
import { CancelServiceaftaleRequest } from 'src/app/api/models/OK/Egis/Service/Models/DTO/cancel-serviceaftale-request';
import { ServiceaftaleDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/serviceaftale-dto';
import { ServiceordreDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/serviceordre-dto';
import { SlutopgoerelseBaseDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/slutopgoerelse-base-dto';
import { TerminationCauseDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/termination-cause-dto';
import { UpcomingChangesDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/upcoming-changes-dto';
import { ServiceaftaleStateEnum } from 'src/app/api/models/OK/Egis/Service/Models/Entities/Enums/serviceaftale-state-enum';
import { GetServiceaftaleResponse } from 'src/app/api/models/OK/Egis/Service/Models/Response/get-serviceaftale-response';
import { ServiceAftaleService, ServiceordreService } from 'src/app/api/services';
import { inOutAnimation } from 'src/app/common/animations';
import { ConfirmDialogComponent } from 'src/app/components/shared/confirm-dialog/confirm-dialog.component';
import { EGIS_SERVICE_CALLER } from 'src/app/constants';
import { ErrorHandlerService } from 'src/app/services/error-handler.service';
import { LoaderService } from 'src/app/services/loader.service';
import { OpsigPr } from './opsig-date/opsig-date.component';
import { KundesegmentEnum } from '../../../../api/models/OK/Egis/Service/Models/Entities/Enums/kundesegment-enum';
import { EftersynStateEnum } from 'src/app/api/models/OK/Egis/Service/Models/DTO/Enums/eftersyn-state-enum';
import { OpkraevningStateEnum } from 'src/app/api/models/OK/Egis/Service/Models/DTO/Enums/opkraevning-state-enum';

@Component({
    selector: 'app-opsig-flow',
    templateUrl: './opsig-flow.component.html',
    styleUrls: ['./opsig-flow.component.scss'],
    animations: [inOutAnimation],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class OpsigFlowComponent implements OnInit {
  @ViewChild('stepper', { static: false }) public flowStepper: MatStepper;

  serviceaftaleStateEnum: typeof ServiceaftaleStateEnum = ServiceaftaleStateEnum;

  opsigPr: typeof OpsigPr = OpsigPr;
  today = new Date();
  endDateTodayOrBefore: boolean = true;

  stepperIndex: number = 0;
  quickViewOpen: boolean = false;

  opsigAftaleForm: UntypedFormGroup;
  opsigDateForm: UntypedFormGroup;
  openOrdersForm: UntypedFormGroup;
  terminationCauseForm: UntypedFormGroup;
  sendLetterForm: UntypedFormGroup;
  slutopgoerelseForm: UntypedFormGroup;

  okKundeAftaleNr: number;
  serviceaftale: ServiceaftaleDto;
  isSpecialAfregningsType: boolean = false;
  serviceOrders: ServiceordreDto[] = [];
  openServiceOrders: ServiceordreDto[] = [];
  validationErrors: any[];
  unsubscribe$ = new Subject<void>();

  ordersToCancel: ServiceordreDto[];
  upcomingChanges?: UpcomingChangesDto;
  terminationCauses: TerminationCauseDto[] = [];
  terminationCause: TerminationCauseDto;

  shouldAllowSlutopg: boolean = true;
  shouldWarnSlutopgInOldInterval: boolean = false;

  isLoading: boolean = false;
  isAtStart: boolean = true;
  isAtEnd: boolean = false;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private dialog: MatDialog,
    private formBuilder: UntypedFormBuilder,
    private errorHandler: ErrorHandlerService,
    private loader: LoaderService,
    private serviceaftaleService: ServiceAftaleService,
    private serviceOrdreService: ServiceordreService,
  ) {}

  currentIntervalEnddate: Date;

  private ShouldWarnAboutSlutopgInOldInterval(serviceaftale: ServiceaftaleDto, enddate: Date): boolean {
    let isInOlderInterval =
      serviceaftale?.currentServiceinterval != null && new Date(enddate) < new Date(serviceaftale.currentServiceinterval?.startDate);
    if (enddate != null && isInOlderInterval) {
      //If the enddate is on an older inteval, we should warn user if currentInterval has eftersyn or opkrævninger
      let hasEftersyn = serviceaftale.currentServiceinterval.eftersynState?.eftersynState != EftersynStateEnum.NotCreated;
      let hasCustomerOpkraevninger = serviceaftale.currentServiceinterval.opkraevningState != OpkraevningStateEnum.NotOpkraevet;
      let hasPartnerOpkraevninger = serviceaftale.currentServiceinterval.partnerOpkraevningState != OpkraevningStateEnum.NotOpkraevet;
      return hasEftersyn || hasCustomerOpkraevninger || hasPartnerOpkraevninger;
    }
    return false;
  }

  private ShouldAllowSlutOpg(serviceaftale: ServiceaftaleDto, enddate: Date): boolean {
    if (serviceaftale == null) {
      return false;
    }
    let isNarvarme = serviceaftale.kunde?.kundeExtendedInformation?.harNaervarmeAftale;
    let isLeasing = serviceaftale.kunde?.kundeExtendedInformation?.harLeasingAftale;
    let isGasfyrTryghed = serviceaftale.kunde?.kundeExtendedInformation?.harGasfyrTryghedAftale;
    let isSpecialAfregningsType = isNarvarme || isLeasing || isGasfyrTryghed;
    let intervalEnddates = serviceaftale.serviceintervaller.filter((i) => i.endDate !== null).map((x) => new Date(x.endDate));

    var isOnEndOfInteval = intervalEnddates.some((x) => x.getTime() === enddate.getTime()) == true;
    return !isOnEndOfInteval && !isSpecialAfregningsType;
  }

  ngOnInit(): void {
    this.okKundeAftaleNr = Number(this.route.snapshot.paramMap.get('okKundeAftaleNr'));
    this.loader.show();
    this.getServiceAftale(this.okKundeAftaleNr)
      .pipe(
        takeUntil(this.unsubscribe$),
        finalize(() => {
          this.loader.hide();
        }),
        flatMap((serviceaftale: ServiceaftaleDto) => {
          this.serviceaftale = serviceaftale;
          let isNarvarme = this.serviceaftale.kunde?.kundeExtendedInformation?.harNaervarmeAftale;
          let isLeasing = this.serviceaftale.kunde?.kundeExtendedInformation?.harLeasingAftale;
          let isGasfyrTryghed = this.serviceaftale.kunde?.kundeExtendedInformation?.harGasfyrTryghedAftale;
          this.isSpecialAfregningsType = isNarvarme || isLeasing || isGasfyrTryghed;
          this.currentIntervalEnddate = new Date(serviceaftale.currentServiceinterval?.endDate);
          this.shouldAllowSlutopg = this.ShouldAllowSlutOpg(serviceaftale, this.opsigDateForm.controls.endDate.value);
          this.shouldWarnSlutopgInOldInterval = this.ShouldWarnAboutSlutopgInOldInterval(
            serviceaftale,
            this.opsigDateForm.controls.endDate.value,
          );

          var createSlutopg = this.shouldAllowSlutopg && !this.shouldWarnSlutopgInOldInterval;
          this.slutopgoerelseForm.controls.createSlutopgoerelse.setValue(createSlutopg);

          if (this.serviceaftale?.serviceaftaleState.state === ServiceaftaleStateEnum.CreatedPending) {
            this.opsigDateForm.controls.endDate.setValue(new Date(serviceaftale.startDate));
            this.opsigDateForm.controls.opsigPr.setValue(OpsigPr.Custom);
            this.opsigDateForm.updateValueAndValidity();
          }

          if (this.serviceaftale.fakturaStop) {
            this.opsigAftaleForm.controls.afregnSlutopgoerelse.disable();
          }

          return forkJoin(this.getOrders(serviceaftale.serviceAftaleNo), this.getUpcomingChanges(serviceaftale.id));
        }),
      )
      .subscribe(([orders, upcomingChanges]) => {
        this.serviceOrders = orders;
        this.openServiceOrders = this.serviceOrders.filter((x) => new Date(x.deadline) >= new Date());
        this.upcomingChanges = upcomingChanges;
      });

    this.serviceaftaleService
      .getTerminationCauses({ caller: EGIS_SERVICE_CALLER })
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(
        (response) => (this.terminationCauses = response),
        (error) => this.errorHandler.HandleError(error),
      );

    this.opsigDateForm = this.formBuilder.group({
      opsigPr: [OpsigPr.Today, [Validators.required]],
      endDate: [new Date(), [Validators.required]],
    });

    this.slutopgoerelseForm = this.formBuilder.group({
      createSlutopgoerelse: [false, Validators.required],
      slutopgoerelseRemark: [null],
      payUntilEndOfPeriod: [true],
      manuelCorrection: [null, [Validators.min(-5000), Validators.max(5000)]],
      addManuelCorrection: [false],
      tilvalgCorrection: [false],
    });

    this.opsigDateForm.controls.endDate.valueChanges.pipe(takeUntil(this.unsubscribe$)).subscribe((value) => {
      this.endDateTodayOrBefore = value <= this.today;
      this.shouldAllowSlutopg = this.ShouldAllowSlutOpg(this.serviceaftale, this.opsigDateForm.controls.endDate.value);
      this.shouldWarnSlutopgInOldInterval = this.ShouldWarnAboutSlutopgInOldInterval(
        this.serviceaftale,
        this.opsigDateForm.controls.endDate.value,
      );

      var createSlutopg = this.shouldAllowSlutopg && !this.shouldWarnSlutopgInOldInterval;
      this.slutopgoerelseForm.controls.createSlutopgoerelse.setValue(createSlutopg);
      this.openServiceOrders = this.serviceOrders.filter((x) => new Date(x.deadline) >= value);
    });

    this.terminationCauseForm = this.formBuilder.group({
      terminationCause: [null, Validators.required],
      terminationCauseOther: [null, Validators.nullValidator],
    });

    this.sendLetterForm = this.formBuilder.group({
      sendLetter: [true, Validators.required],
    });

    this.openOrdersForm = this.formBuilder.group({
      terminationCauseForServiceordre: ['Annulleres da serviceaftale er opsagt', [Validators.required]],
    });

    this.opsigAftaleForm = this.formBuilder.group({
      opsigDateForm: this.opsigDateForm,
      terminationCauseForm: this.terminationCauseForm,
      sendLetterForm: this.sendLetterForm,
      openOrdersForm: this.openOrdersForm,
      slutopgoerelseForm: this.slutopgoerelseForm,
      afregnSlutopgoerelse: [false],
    });

    this.terminationCauseForm.controls.terminationCause.valueChanges.pipe(takeUntil(this.unsubscribe$)).subscribe((val) => {
      if (val) {
        this.terminationCause = this.terminationCauses.find((x) => x.entityId == val);
      } else {
        this.terminationCause = null;
      }
    });

    this.loader.loadingStateChanged$.subscribe((isloading) => (this.isLoading = isloading));
  }

  ngAfterViewInit() {
    this.flowStepper.selectionChange.subscribe((event) => {
      this.isAtStart = event.selectedIndex === 0;
      this.isAtEnd = event.selectedIndex === this.flowStepper.steps.length - 1;
    });
  }

  get terminationCauseOtherName(): string {
    return 'Andet';
  }

  get endDate(): Date {
    return this.opsigDateForm.get('endDate').value;
  }

  get selectedOpsigPr(): OpsigPr {
    return this.opsigDateForm.get('opsigPr').value;
  }

  getServiceAftale(okKundeAftaleNr: number): Observable<ServiceaftaleDto> {
    return this.serviceaftaleService
      .getServiceAftaleForKunde({
        okKundeAftaleNr: okKundeAftaleNr,
        caller: EGIS_SERVICE_CALLER,
      })
      .pipe(map((response: GetServiceaftaleResponse) => response.serviceaftale));
  }

  getOrders(serviceaftaleNo: number): Observable<ServiceordreDto[]> {
    return this.serviceOrdreService.getServiceordrerForServiceaftale({ serviceaftaleNo: serviceaftaleNo, caller: EGIS_SERVICE_CALLER });
  }

  getUpcomingChanges(serviceaftaleId: number): Observable<UpcomingChangesDto> {
    return this.serviceaftaleService.getUpcomingChanges({
      serviceaftaleId: serviceaftaleId,
      caller: EGIS_SERVICE_CALLER,
    });
  }

  reloadServiceAftale() {
    this.loader.show;
    this.getServiceAftale(this.okKundeAftaleNr)
      .pipe(
        takeUntil(this.unsubscribe$),
        finalize(() => {
          this.loader.hide();
        }),
      )
      .subscribe((serviceaftale) => {
        this.serviceaftale = serviceaftale;
        this.currentIntervalEnddate = new Date(serviceaftale.currentServiceinterval?.endDate);
      });
  }

  onCloseClick() {
    if (!this.opsigAftaleForm.dirty) {
      this.router.navigate(['/aftale', this.okKundeAftaleNr], { fragment: 'Abonnement' });
    } else {
      let dialogRef = this.dialog.open(ConfirmDialogComponent, {
        data: {
          title: 'Er du sikker?',
          body: 'Er du sikker på at du vil afbryde?',
        },
      });

      dialogRef.afterClosed().subscribe((confirm) => {
        if (confirm) {
          this.router.navigate(['/aftale', this.okKundeAftaleNr], { fragment: 'Abonnement' });
        }
      });
    }
  }

  onCancelOrderChanged(serviceordreNos: number[]) {
    this.ordersToCancel = this.serviceOrders.filter((order) => serviceordreNos.find((x) => x == order.serviceordreNo) > 0);
  }

  goBack() {
    if (this.flowStepper.selectedIndex != 0) {
      this.flowStepper.previous();
    }
  }

  goToStep(stepNumber: number) {
    this.flowStepper.selectedIndex = stepNumber;
  }

  momsTekst() {
    const moms = this.serviceaftale?.kunde?.kundetype?.kundeSegment === KundesegmentEnum.Privat ? 'inkl. moms' : 'ekskl. moms';
    return `Alle beløb er ${moms}`;
  }

  save() {
    let slutopgoerelseBase: SlutopgoerelseBaseDto = {
      slutopgoerelseRemark: this.slutopgoerelseForm.controls.slutopgoerelseRemark.value,
      payUntilEndOfPeriod: this.slutopgoerelseForm.controls.payUntilEndOfPeriod.value,
      manuelCorrectionAmount: this.slutopgoerelseForm.controls.addManuelCorrection.value
        ? this.slutopgoerelseForm.controls.manuelCorrection.value
        : null,
      correctForFrieTilkald: this.slutopgoerelseForm.controls.tilvalgCorrection.value,
    };
    let request: CancelServiceaftaleRequest = {
      endDate: this.endDate.toOkDateString(),
      serviceaftaleId: this.serviceaftale.id,
      rowVersion: this.serviceaftale.rowVersion,
      terminationCauseEntityId:
        this.terminationCauseForm.controls.terminationCause.value != this.terminationCauseOtherName
          ? this.terminationCauseForm.controls.terminationCause.value
          : null,
      terminationCauseOther: this.terminationCauseForm.controls.terminationCauseOther.value,
      sendCancelLetter: this.sendLetterForm.controls.sendLetter.value,
      serviceOrderNosToCancel: this.ordersToCancel.map((x) => x.serviceordreNo),
      createSlutopgoerelse: this.slutopgoerelseForm.controls.createSlutopgoerelse.value,
      slutopgoerelse: slutopgoerelseBase,
      afregnSlutopgoerelse:
        this.slutopgoerelseForm.controls.createSlutopgoerelse.value && this.opsigAftaleForm.controls.afregnSlutopgoerelse.value,
      terminationCauseForServiceordre: this.openOrdersForm.controls.terminationCauseForServiceordre.value,
    };

    this.loader.show();
    this.serviceaftaleService
      .cancelServiceaftale({
        caller: EGIS_SERVICE_CALLER,
        body: request,
      })
      .pipe(
        takeUntil(this.unsubscribe$),
        finalize(() => {
          this.loader.hide();
        }),
      )
      .subscribe(
        (serviceaftale: ServiceaftaleDto) => {
          //this.router.navigate(['/aftale', serviceaftale.okKundeAftaleNr], { fragment: 'Abonnement' });
          this.goToStep(this.flowStepper._steps.length - 1);
        },
        (error: HttpErrorResponse) => {
          this.errorHandler.HandleError(error);
        },
      );
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.unsubscribe();
  }
}
